/**
 * Sheet: depth on demand. A framed panel that slides in from the right: a header (title, a line under
 * it, actions, Close), a scrolling body and an optional footer. Non-modal unless `modal`: the page
 * behind stays live and Tab still cycles inside. Esc, or a press on plain page outside it, closes it and
 * focus goes back to what opened it; a press on a control outside it (a row, a link, a button) leaves the
 * control to do its job, and a sheet that job opens replaces this one, so there is never a sheet behind a sheet.
 * From `lg` it opens under the top bar, which stays in reach; `wide` gives a review its table's width (never past the
 * screen, less the 12 px either side); `fit` ends it where its content ends
 * and stands on the menus' elevation (a full-height sheet's long shadow would spill under a short one).
 * The surface and its content arrive together: it is opaque and filled from its first frame, and only slides.
 */
import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { Dialog as Primitive } from "radix-ui";
import { Button } from "./Button";
import { CloseIcon } from "./CloseIcon";
import { cx } from "./cx";
import { DRAWER_OPENS, useFocusReturn } from "./overlay";

/**
 * The real controls: a press on one outside the sheet leaves the sheet to it. The control does its job, and if that
 * job opens another sheet, that sheet takes this one's place (app/oneSheet.ts). A press anywhere else (plain page, a
 * chart, the gaps of a group, a table's empty row) closes the sheet, so a close (a step back, like Esc's) never runs
 * in the same press as another step through the history. Nothing counts for being merely focusable.
 */
const CONTROLS = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  ...["button", "link", "tab", "menuitem", "menuitemcheckbox", "menuitemradio", "checkbox", "radio", "switch", "option", "combobox"].map((role) => `[role="${role}"]`),
  // A row of a table whose rows open a sheet (not its empty-state row).
  '[data-sheet-opener] [role="rowgroup"] > [role="row"]:not([data-empty])',
  // A field's whole frame (the sidebar's search pill: its icon and padding open the palette as its input does).
  '[data-slot="input-group"]',
  // A toast: a near miss on its Undo is still the toast's.
  '[data-slot="toast"]',
].join(", ");

const isControl = (target: Element): boolean => target.closest(CONTROLS) !== null;

/**
 * A layer standing over the page and the sheet: a modal (the palette, Help, Feedback), or a menu or list that is
 * open (one closing is not: a press just after it is the page's). A press anywhere while one stands is that layer's:
 * it closes the layer, never the sheet. (A modal takes the page's pointer events, so its outside press lands on
 * <html>: where the press lands cannot tell.)
 */
const LAYER_OVER = [
  // Radix marks a modal by taking the page's pointer events, not by aria-modal: any open dialog but a sheet is one.
  '[role="dialog"][data-state="open"]:not([data-slot="sheet"])',
  '[role="alertdialog"][data-state="open"]',
  // The shell's drawer below lg (inert while closed).
  '[aria-modal="true"]:not([inert])',
  '[data-radix-popper-content-wrapper] :is([role="menu"], [role="listbox"])[data-state="open"]',
].join(", ");
/** Inside another layer (a dialog, a menu, a tooltip): not on the page. */
const OTHER_LAYER = '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]';
/** Presses closer together than this are one close: a double-click, or Esc just after a press. */
const CLOSE_MOMENT_MS = 500;
/** How far a finger may move between touching and lifting and still have pressed (beyond it, it scrolled). */
const TAP_SLOP = 10;

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One muted line under the title. */
  description?: ReactNode;
  /** Controls in the header, left of Close. */
  actions?: ReactNode;
  /** Pinned under the body: the sheet's primary action. */
  footer?: ReactNode;
  /** A locked, inert page and a real focus trap, for a sheet that must be answered. */
  modal?: boolean;
  /** As tall as its content, for a few lines of detail; otherwise it runs to the foot of the page. */
  fit?: boolean;
  /** Wide enough for a table of a few columns (`--spacing-sheet-wide`): a review, not a detail. */
  wide?: boolean;
  /** True while the body's data is loading. */
  busy?: boolean;
  /** Where focus goes on close, when it should not be what opened the sheet (the row now shown). */
  restoreFocus?: () => HTMLElement | null;
  children: ReactNode;
  className?: string;
}

export function Sheet({ open, onClose, title, description, actions, footer, modal = false, fit = false, wide = false, busy = false, restoreFocus, children, className }: SheetProps) {
  const focus = useFocusReturn({ restore: restoreFocus });
  const content = useRef<HTMLDivElement>(null);
  // Once asked to close, it is closing: a second press (a double-click, Esc after a press) must not close it again,
  // which would step back twice and leave the page.
  const closing = useRef(false);
  const rearm = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (open) return;
    window.clearTimeout(rearm.current);
    closing.current = false;
  }, [open]);
  useEffect(() => () => window.clearTimeout(rearm.current), []);
  const latest = useRef(onClose);
  useEffect(() => {
    latest.current = onClose;
  });
  const close = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    latest.current();
    // Only the presses of one moment are one close: if the sheet is still open after it, the next press asks again,
    // so a close that did not take never leaves the sheet deaf (#716).
    rearm.current = window.setTimeout(() => (closing.current = false), CLOSE_MOMENT_MS);
  }, []);

  // Non-modal: the press outside is judged here, at the press, before any layer reacts to it. Radix's own
  // pointerDownOutside (deferred to the click for a dialog) does not fire for the first press after a menu inside the
  // sheet has closed, in Chromium: it takes a third press to close the Filter sheet. It is not relied on.
  // A mouse press closes at the press; a finger or a pen only when it lifts where it touched, so starting a scroll beside
  // the sheet leaves it be (a scroll never ends in a click). The shell's drawer opening closes it too: one panel at a time.
  useEffect(() => {
    if (!open || modal) return;
    let tap: { x: number; y: number } | null = null;
    const onPointerDown = (event: PointerEvent) => {
      tap = null;
      const target = event.target;
      if (!(target instanceof Element) || content.current?.contains(target) || target.closest(OTHER_LAYER)) return;
      if (document.querySelector(LAYER_OVER) || isControl(target)) return;
      if (event.pointerType === "touch" || event.pointerType === "pen") tap = { x: event.clientX, y: event.clientY };
      else close();
    };
    const onClick = (event: MouseEvent) => {
      if (tap && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) <= TAP_SLOP) close();
      tap = null;
    };
    const forget = () => (tap = null);
    document.addEventListener(DRAWER_OPENS, close);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointercancel", forget, true);
    return () => {
      document.removeEventListener(DRAWER_OPENS, close);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointercancel", forget, true);
    };
  }, [open, modal, close]);

  return (
    <Primitive.Root open={open} modal={modal} onOpenChange={(next) => !next && close()}>
      <Primitive.Portal>
        <Primitive.Content
          data-slot="sheet"
          aria-busy={busy || undefined}
          {...(description ? {} : { "aria-describedby": undefined })}
          tabIndex={-1}
          {...focus}
          ref={content}
          // Non-modal: a press outside is judged by the listener above; focus moving out (Tab is trapped, the palette) never closes it.
          onPointerDownOutside={(event) => !modal && event.preventDefault()}
          onFocusOutside={(event) => !modal && event.preventDefault()}
          className={cx(
            "fixed right-3 top-3 z-40 flex flex-col rounded-xl border-hair border-border bg-muted p-1 outline-none data-[state=open]:animate-sheet-in data-[state=closed]:animate-sheet-out",
            wide ? "w-[min(var(--spacing-sheet-wide),calc(100vw-24px))]" : "w-[min(var(--spacing-sheet),calc(100vw-24px))]",
            // A modal sheet answers before anything else, so it keeps the full height.
            !modal && "lg:top-topbar",
            fit ? "max-h-[calc(100dvh-var(--spacing-topbar)-12px)] shadow-popover" : "bottom-3 shadow-drawer",
            className,
          )}
        >
          <header className="flex shrink-0 items-start justify-between gap-3 px-3 py-2.5">
            <div className="flex min-w-0 flex-col gap-1 py-1">
              <Primitive.Title className="text-heading font-medium text-foreground">{title}</Primitive.Title>
              {description && <Primitive.Description className="text-meta text-secondary-foreground">{description}</Primitive.Description>}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {actions}
              <Button variant="ghost" size="icon-sm" aria-label="Close" onClick={close}>
                <CloseIcon />
              </Button>
            </div>
          </header>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-xl border-hair border-input bg-card px-4 pb-4 pt-5 [scrollbar-width:thin]">
            {/* Grows with its content (the body scrolls), or hands its height to a child that scrolls itself (`min-h-0 flex-1`). */}
            <div className="flex flex-1 flex-col">{children}</div>
          </div>
          {footer && (
            <footer className="flex shrink-0 items-center justify-end gap-2 px-2 pb-1.5 pt-2.5">
              {footer}
            </footer>
          )}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}

/** A titled block inside a sheet's body; blocks are separated by a hairline. */
export function SheetSection({ title, children, className }: { title?: string; children: ReactNode; className?: string }) {
  return (
    <section className={cx("flex w-full flex-col gap-3 border-b border-rule py-5 first:pt-0 last:border-b-0 last:pb-0", className)}>
      {title && <h3 className="text-caption font-medium uppercase leading-none tracking-[0.04em] text-muted-foreground">{title}</h3>}
      {children}
    </section>
  );
}

/** Label and value rows for a sheet: `rows` of [label, value]. */
export function SheetRows({ rows }: { rows: ReadonlyArray<readonly [label: string, value: ReactNode]> }) {
  return (
    <dl className="flex w-full flex-col gap-3.5">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-baseline justify-between gap-4 text-body leading-5">
          <dt className="shrink-0 text-muted-foreground">{label}</dt>
          <dd className="min-w-0 text-right font-medium break-words text-cell-foreground tabular">{value}</dd>
        </div>
      ))}
    </dl>
  );
}
